import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Home, Users, Eye, Plus, Power, Trash2, MessageSquare, ShieldAlert, ArrowLeft } from 'lucide-react';
import { t } from '../../utils/i18n';
import { useDmPersona } from '../../context/DmPersonaContext';
import {
  getMyCelebRoom,
  createCelebRoom,
  disableCelebRoom,
  deleteCelebRoom,
  setFanListVisibility,
} from '../../api/celebRoom';

function formatDateTime(value) {
  if (!value) return '-';
  return String(value).replace('T', ' ').slice(0, 19);
}

export default function MyCelebRoom() {
  const queryClient = useQueryClient();
  const { accessToken, userId } = useDmPersona();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [actionError, setActionError] = useState(null);

  const roomQuery = useQuery({
    queryKey: ['celeb-room', 'mine', userId],
    queryFn: () => getMyCelebRoom(accessToken),
    enabled: !!accessToken,
    retry: false,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['celeb-room', 'mine', userId] });

  const createMut = useMutation({
    mutationFn: () => createCelebRoom(accessToken),
    onSuccess: () => { setActionError(null); invalidate(); },
    onError: (e) => setActionError(e?.message || t('celebRoom.mine.errors.createFailed')),
  });
  const disableMut = useMutation({
    mutationFn: (roomId) => disableCelebRoom(roomId, accessToken),
    onSuccess: () => { setActionError(null); invalidate(); },
    onError: (e) => setActionError(e?.message || t('celebRoom.mine.errors.actionFailed')),
  });
  const visibilityMut = useMutation({
    mutationFn: ({ roomId, visible }) => setFanListVisibility(roomId, visible, accessToken),
    onSuccess: () => { setActionError(null); invalidate(); },
    onError: (e) => setActionError(e?.message || t('celebRoom.mine.errors.actionFailed')),
  });
  const deleteMut = useMutation({
    mutationFn: (roomId) => deleteCelebRoom(roomId, accessToken),
    onSuccess: () => { setActionError(null); setConfirmDelete(false); invalidate(); },
    onError: (e) => setActionError(e?.message || t('celebRoom.mine.errors.actionFailed')),
  });

  const room = roomQuery.data;
  const err = roomQuery.error;
  const notCeleb = err?.code === -460 || err?.status === 403;
  const noRoom = err?.code === -464 || err?.status === 404;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <Link
        to="/celeb-room/feed"
        className="inline-flex items-center gap-2 text-sm text-dark-text-secondary hover:text-primary mb-6"
      >
        <ArrowLeft size={16} />
        {t('celebRoom.mine.backToFeed')}
      </Link>
      <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2 mb-6">
        <Home size={20} className="text-primary" />
        {t('celebRoom.mine.title')}
      </h1>

      {actionError && (
        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
          {actionError}
        </div>
      )}

      {roomQuery.isLoading ? (
        <div className="text-center py-16 text-dark-text-secondary">{t('common.loading')}</div>
      ) : notCeleb ? (
        <div className="text-center py-12 px-6 rounded-2xl bg-dark-card border border-dark-border">
          <ShieldAlert size={32} className="mx-auto mb-3 text-dark-text-muted" />
          <p className="text-sm text-dark-text-secondary">{t('celebRoom.mine.notCeleb')}</p>
        </div>
      ) : noRoom ? (
        <div className="text-center py-12 px-6 rounded-2xl bg-dark-card border border-dark-border">
          <Plus size={32} className="mx-auto mb-3 text-primary" />
          <p className="text-sm text-dark-text-secondary mb-5">{t('celebRoom.mine.noRoom')}</p>
          <button
            type="button"
            onClick={() => createMut.mutate()}
            disabled={createMut.isPending}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-black text-sm font-bold hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            <Plus size={16} />
            {createMut.isPending ? t('common.loading') : t('celebRoom.mine.createRoom')}
          </button>
        </div>
      ) : roomQuery.isError ? (
        <div className="text-center py-12 px-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
          {err?.message || t('celebRoom.mine.errors.loadFailed')}
        </div>
      ) : room ? (
        <div className="space-y-5">
          <div className="rounded-2xl bg-dark-card border border-dark-border p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs text-dark-text-muted">{t('celebRoom.mine.roomId')}</p>
                <p className="text-lg font-bold text-white">#{room.room_id}</p>
              </div>
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                  room.status === 'active'
                    ? 'bg-green-500/10 text-green-400 border border-green-500/30'
                    : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30'
                }`}
              >
                {room.status === 'active'
                  ? t('celebRoom.status.active')
                  : t('celebRoom.status.disabled')}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="rounded-lg bg-dark-surface border border-dark-border p-3">
                <p className="text-xs text-dark-text-muted flex items-center gap-1 mb-1">
                  <Users size={12} /> {t('celebRoom.mine.fanCount')}
                </p>
                <p className="text-lg font-semibold text-white">{room.enter_fan_count ?? 0}</p>
              </div>
              <div className="rounded-lg bg-dark-surface border border-dark-border p-3">
                <p className="text-xs text-dark-text-muted flex items-center gap-1 mb-1">
                  <Eye size={12} /> {t('celebRoom.mine.viewCount')}
                </p>
                <p className="text-lg font-semibold text-white">{room.view_count ?? 0}</p>
              </div>
            </div>

            <dl className="text-xs text-dark-text-secondary space-y-1 mb-4">
              <div className="flex justify-between">
                <dt>{t('celebRoom.mine.lastMessageAt')}</dt>
                <dd>{formatDateTime(room.last_message_at)}</dd>
              </div>
              <div className="flex justify-between">
                <dt>{t('celebRoom.mine.createdAt')}</dt>
                <dd>{formatDateTime(room.created_at)}</dd>
              </div>
            </dl>

            <Link
              to={`/celeb-room/r/${room.room_id}`}
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-primary text-black text-sm font-bold hover:bg-primary/90 transition-colors"
            >
              <MessageSquare size={16} />
              {t('celebRoom.mine.openRoom')}
            </Link>
          </div>

          <div className="rounded-2xl bg-dark-card border border-dark-border p-5 space-y-4">
            <p className="text-sm font-semibold text-white">{t('celebRoom.mine.settings')}</p>

            <label className="flex items-center justify-between gap-3 cursor-pointer select-none">
              <span className="text-sm text-dark-text-secondary">
                {t('celebRoom.mine.fanListVisible')}
              </span>
              <input
                type="checkbox"
                checked={Boolean(room.is_fan_list_visible)}
                disabled={visibilityMut.isPending}
                onChange={(e) =>
                  visibilityMut.mutate({ roomId: room.room_id, visible: e.target.checked })
                }
                className="w-4 h-4 rounded border-dark-border bg-dark-surface accent-primary cursor-pointer"
              />
            </label>

            <div className="flex flex-wrap gap-2 pt-1">
              <Link
                to={`/celeb-room/r/${room.room_id}/fans`}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-dark-surface border border-dark-border text-dark-text-secondary text-sm hover:border-primary/40 hover:text-primary transition-colors"
              >
                <Users size={14} />
                {t('celebRoom.mine.viewFans')}
              </Link>
              {room.status === 'active' && (
                <button
                  type="button"
                  onClick={() => disableMut.mutate(room.room_id)}
                  disabled={disableMut.isPending}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-dark-surface border border-yellow-500/40 text-yellow-300 text-sm hover:bg-yellow-500/10 transition-colors disabled:opacity-50"
                >
                  <Power size={14} />
                  {t('celebRoom.mine.disable')}
                </button>
              )}
              {!confirmDelete ? (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-dark-surface border border-red-500/40 text-red-300 text-sm hover:bg-red-500/10 transition-colors"
                >
                  <Trash2 size={14} />
                  {t('celebRoom.mine.delete')}
                </button>
              ) : (
                <div className="inline-flex items-center gap-2">
                  <span className="text-xs text-red-300">{t('celebRoom.mine.deleteConfirm')}</span>
                  <button
                    type="button"
                    onClick={() => deleteMut.mutate(room.room_id)}
                    disabled={deleteMut.isPending}
                    className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-bold hover:bg-red-600 transition-colors disabled:opacity-50"
                  >
                    {deleteMut.isPending ? t('common.loading') : t('celebRoom.mine.deleteYes')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    className="px-3 py-1.5 rounded-lg border border-dark-border text-dark-text-secondary text-xs hover:text-white transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
